import type { MainNavRoute, MainPath } from '../types';

import PERMISSIONS from '@snowit/utilities/permissions';

import { makeChildren } from '../utils';

export const CATEGORIES_TABS = ['info', 'webshop', 'config', 'subcategories'] as const;
export type CategoriesTabs = (typeof CATEGORIES_TABS)[number];

export const TAGS_TABS = ['info', 'webshop'] as const;
export type TagsTabs = (typeof TAGS_TABS)[number];

export const DISCOUNTS_TABS = ['info', 'config', 'conditions', 'usages'] as const;
export type DiscountsTabs = (typeof DISCOUNTS_TABS)[number];

export const AVAILABILITY_TABS = ['product', 'stock'] as const;
export type AvailabilityTabs = (typeof AVAILABILITY_TABS)[number];

export const OptionsPath: MainPath = '/product_options/:id?/:section?';
export const CategoriesPath: MainPath = '/categories/:id?/:section?/:subId?';
export const TagsPath: MainPath = '/tags/:id?/:section?';
export const DiscountsPath: MainPath = '/discounts/:id?/:section?';
export const ExternalCodesPath: MainPath = '/external_codes/:id?/:section?';
export const CouponPath: MainPath = '/coupons/:id?/:section?';
export const SeasonsPath: MainPath = '/seasons/:id?/:section?';
export const StagesPath: MainPath = '/stages/:id?/:section?';
export const AvailabilityPath: MainPath = '/availability/:type?/:id?';

export const OptionsRoute: MainNavRoute = {
  path: '/product_options',
  label: 'routes.product_options',
  icon: 'grid_plus',
  permissions: [PERMISSIONS.PRODUCT_OPTIONS__LIST],
  type: 1,
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.PRODUCT_OPTIONS__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23861/420faea65098b94',
};

export const CategoriesRoute: MainNavRoute = {
  path: '/categories',
  label: 'routes.categories',
  icon: 'category',
  permissions: [PERMISSIONS.CATEGORIES__LIST],
  type: 1,
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.CATEGORIES__DETAILS],
      children: makeChildren<CategoriesTabs>({
        info: { path: 'info' },
        webshop: { path: 'webshop' },
        config: { path: 'config' },
        subcategories: { path: 'subcategories', children: [{ path: ':id' }] },
      }),
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23821/2471634d0f47012',
};

export const TagsRoute: MainNavRoute = {
  path: '/tags',
  label: 'routes.tags',
  icon: 'tag',
  permissions: [PERMISSIONS.TAGS__LIST],
  type: 1,
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.TAGS__DETAILS],
      children: makeChildren<TagsTabs>({
        info: { path: 'info' },
        webshop: { path: 'webshop' },
      }),
    },
  ],
};

export const DiscountsRoute: MainNavRoute = {
  path: '/discounts',
  label: 'routes.discounts',
  icon: 'percent',
  permissions: [PERMISSIONS.DISCOUNTS__LIST],
  type: 1,
  children: [
    {
      path: ':id',
      disowned: true,
      permissions: [PERMISSIONS.DISCOUNTS__DETAILS, PERMISSIONS.DISCOUNTS__CREATE],
      logic: 'OR',
      children: makeChildren<DiscountsTabs>({
        info: { path: 'info' },
        config: { path: 'config' },
        conditions: { path: 'conditions' },
        usages: { path: 'usages' },
      }),
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23841/549fb9bda0c9840',
};

export const ExternalCodesRoute: MainNavRoute = {
  path: '/external_codes',
  label: 'routes.external_codes',
  icon: 'percent',
  permissions: [PERMISSIONS.EXTERNAL_CODES__LIST],
  type: 1,
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.EXTERNAL_CODES__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23901/c8846fc3729bd7e',
};

export const CouponRoutes: MainNavRoute = {
  path: '/coupons',
  label: 'routes.coupons',
  permissions: [PERMISSIONS.COUPONS__LIST],
  icon: 'ticket',
  type: 1,
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.COUPONS__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23881/dae957ebcd47ae7',
};

export const SeasonsRoute: MainNavRoute = {
  path: '/seasons',
  label: 'routes.seasons',
  permissions: [PERMISSIONS.SEASONS__LIST],
  icon: 'calendar_week',
  type: 1,
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.SEASONS__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23921/e37f9358ead0cc0',
};

export const StagesRoute: MainNavRoute = {
  path: '/stages',
  label: 'routes.stages',
  icon: 'route',
  type: 1,
  permissions: [PERMISSIONS.STAGES__LIST],
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.STAGES__DETAILS],
    },
  ],
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23941/3716a85b003afa9',
};

export const AvailabilityRoute: MainNavRoute = {
  path: '/availability',
  label: 'routes.availability',
  icon: 'check_square',
  permissions: [PERMISSIONS.AVAILABILITY__VIEW],
  type: 1,
  children: makeChildren<AvailabilityTabs>({
    product: {
      path: 'product',
      children: [{ path: ':id' }],
    },
    stock: {
      path: 'stock',
      children: [{ path: ':id' }],
    },
  }),
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23961/d96687cd1a51d57',
};
